import rawFetch from 'isomorphic-fetch'
import merge from 'lodash/merge'
import redirectToLogin from './redirect'

const DEFAULT_OPTIONS = Object.freeze({
  redirect: 'follow',
  credentials: 'include',
  mode: 'cors',
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

export { rawFetch }

export async function fetch (url, options) {
  options = merge({}, DEFAULT_OPTIONS, options)
  const r = await rawFetch(url, options)
  r._json = async () => {
    const text = await r.text()
    if (!text) return
    return JSON.parse(text)
  }
  r.json = async function () {
    if (r.bodyUsed) return r._body
    const body = await r._json()
    r._body = body
    return body
  }
  if (!r.ok) return handleErrorResponse(r)
  return r
}

export async function fetchJSON (url, options = {}) {
  const r = await fetch(url, options)
  return r.json()
}

export async function handleErrorResponse (response) {
  if (response.status === 401) {
    if (typeof window === 'undefined') return
    if (window.location.pathname === '/') {
      window.location = '/login'
    } else if (!window.location.pathname.startsWith('/login')) {
      redirectToLogin()
    }
    return
  }
  // try extract error message from body
  const text = await response.text().catch(err => {
    throw new ResponseError(response, err)
  })
  let data = null
  try { data = JSON.parse(text) } catch (_) {}
  if (!data) throw new ResponseError(response, text)

  const { error, message, errors } = data
  // handle error/message properties
  if (error || message) throw new ResponseError(response, getErrorMessage(error, message), data)
  // handle array of errors
  if (errors && errors.length) throw new ResponseError(response, errors.map(err => err.message).join('\n'), data)
  // default to just body text
  throw new ResponseError(response, text, data)
}

class ResponseError extends Error {
  constructor (response, message = '', props = {}) {
    if (message.stack) message = message.stack
    message = message.trim().slice(0, MAX_TEXT_ERROR_LENGTH)
    if (message.startsWith('<!')) message = ''
    super(`${response.status} – ${response.statusText || ''}: ${response.url}\n${message}`.trim())
    if (typeof this.constructor.captureStackTrace === 'function') this.constructor.captureStackTrace(this, this.constructor.name)

    const code = Number(response.statusText) || Number(response.status) || Number(response.statusCode) || 500

    Object.assign(this, {
      name: this.constructor.name,
      url: response.url,
      response,
      // status code aliases
      code,
      status: code,
      statusCode: code,
      statusText: response.statusText || code
    }, props, {
      message
    })
  }
}

const MAX_TEXT_ERROR_LENGTH = 350
function getErrorMessage (error, message = '') {
  const errorText = `${error || ''} `.trim().slice(0, MAX_TEXT_ERROR_LENGTH)
  return errorText + message.trim().slice(0, MAX_TEXT_ERROR_LENGTH)
}

function getTotal (res) {
  return Number(res.headers.get('Content-Length') || res.headers.get('x-Content-Length'))
}

export function getProgressStream (res, onProgress) {
  if (!onProgress) return res
  const total = getTotal(res)
  let progress = 0
  return getStream(res, ({ done, value }) => {
    if (done) return
    progress += value.length
    onProgress({ progress, total })
  })
}

export function getStream (res, onData) {
  if (!res.body.getReader || !onData) {
    return res
  }
  const reader = res.body.getReader()
  return new window.Response(new window.ReadableStream({
    start (controller) {
      return pump()
      async function pump () {
        const { done, value } = await reader.read()
        onData({ done, value })
        if (done) {
          controller.close()
          return
        }
        controller.enqueue(value)
        return pump()
      }
    }
  }))
}



// WEBPACK FOOTER //
// ../webmango-utils/fetch.js